import { useMemo, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { PROJECTS } from "@/data/content";
import { ArrowUpRight } from "lucide-react";

const FILTER_KEYS = ["all", "residential", "commercial", "land", "mixed"];

export default function Projects() {
  const { t, lang } = useLang();
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return PROJECTS;
    return PROJECTS.filter((p) => p.category === active);
  }, [active]);

  return (
    <section id="projects" className="section-y bg-[color:var(--bone)]" data-testid="projects-section">
      <div className="container-lux">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="overline" data-testid="projects-overline">{t.projects.overline}</div>
            <h2
              className="mt-6 font-serif-display text-[clamp(2rem,4.5vw,3.75rem)] leading-[1.05] tracking-[-0.01em] text-[color:var(--anthracite)]"
              data-testid="projects-title"
            >
              {t.projects.title}
            </h2>
          </div>

          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter">
            {FILTER_KEYS.map((k) => (
              <button
                key={k}
                className={`chip ${active === k ? "active" : ""}`}
                onClick={() => setActive(k)}
                data-testid={`project-filter-${k}`}
                role="tab"
                aria-selected={active === k}
              >
                {t.projects.filters[k]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((p, idx) => (
            <article
              key={p.id}
              className={`group proj-frame ${idx % 5 === 0 ? "md:col-span-2 md:row-span-1" : ""}`}
              style={{ aspectRatio: idx % 5 === 0 ? "16 / 10" : "4 / 5" }}
              data-testid={`project-card-${p.id}`}
            >
              <img
                src={p.image}
                alt={p.title[lang] || p.title.en}
                className="img-cover img-zoom"
                loading="lazy"
              />
              <div className="proj-caption">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--bronze)]">
                  {t.projects.filters[p.category]} · {p.year}
                </div>
                <h3 className="mt-2 font-serif-display text-[26px] leading-tight">
                  {p.title[lang] || p.title.en}
                </h3>
                <div className="mt-1 text-[13px] text-[color:var(--bone)]/80">
                  {p.location[lang] || p.location.en}
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <p className="text-[13.5px] leading-[1.6] text-[color:var(--bone)]/75 max-w-md">
                    {p.lede[lang] || p.lede.en}
                  </p>
                  <ArrowUpRight
                    size={22}
                    strokeWidth={1}
                    className="flex-none text-[color:var(--bronze)] group-hover:rotate-45 transition-transform duration-500"
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
